import { useCallback } from 'react';
import { Recipe } from '../../../api/Types';
import apiService from '../../../api/apiService';

interface ResolveImageForSubmitResult {
  imageUrl: string | null;
  shouldAbort: boolean;
}

interface UseEditRecipeFormActionsParams {
  recipe: Recipe;
  title: string;
  ingredients: string;
  steps: string;
  selectedTagIds: number[];
  isAuthor: boolean;
  token: string | null | undefined;
  resolveImageForSubmit: () => Promise<ResolveImageForSubmitResult>;
  triggerAiGenerationIfNeeded: (imageUrl: string | null) => void;
  onRecipeUpdated: () => void;
  onRecipeDeleted: () => void;
  setSubmitting: (submitting: boolean) => void;
  resetFeedback: () => void;
  setFeedback: (message: string, isError?: boolean) => void;
}

export const useEditRecipeFormActions = ({
  recipe,
  title,
  ingredients,
  steps,
  selectedTagIds,
  isAuthor,
  token,
  resolveImageForSubmit,
  triggerAiGenerationIfNeeded,
  onRecipeUpdated,
  onRecipeDeleted,
  setSubmitting,
  resetFeedback,
  setFeedback,
}: UseEditRecipeFormActionsParams) => {
  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isAuthor || !token) {
      return;
    }

    setSubmitting(true);
    resetFeedback();

    const { imageUrl, shouldAbort } = await resolveImageForSubmit();
    if (shouldAbort) {
      return;
    }

    const updatedRecipeData = { ...recipe, title, ingredients, steps, image: imageUrl };

    try {
      await apiService.updateRecipe(updatedRecipeData, selectedTagIds);
      triggerAiGenerationIfNeeded(imageUrl);
      setFeedback('Recette mise à jour avec succès !');
      onRecipeUpdated();
    } catch (error: any) {
      setFeedback(`Erreur : ${error.message}`, true);
    } finally {
      setSubmitting(false);
    }
  }, [
    ingredients,
    isAuthor,
    onRecipeUpdated,
    recipe,
    resetFeedback,
    resolveImageForSubmit,
    selectedTagIds,
    setFeedback,
    setSubmitting,
    steps,
    title,
    token,
    triggerAiGenerationIfNeeded,
  ]);

  const handleDelete = useCallback(async () => {
    if (!isAuthor || !token || !window.confirm(`Supprimer "${recipe.title}" ?`)) {
      return;
    }

    setSubmitting(true);
    resetFeedback();

    try {
      await apiService.deleteRecipe(recipe.id);
      setFeedback('Recette supprimée.');
      onRecipeDeleted();
    } catch (error: any) {
      setFeedback(`Erreur : ${error.message}`, true);
    } finally {
      setSubmitting(false);
    }
  }, [
    isAuthor,
    onRecipeDeleted,
    recipe.id,
    recipe.title,
    resetFeedback,
    setFeedback,
    setSubmitting,
    token,
  ]);

  return {
    handleSubmit,
    handleDelete,
  };
};

export default useEditRecipeFormActions;
